/**
 * appLinkHref — очистка адреса для ДВУХ кнопок, ведущих в приложение
 * (WEB-1911, находка 8 ревизии пачки 18).
 *
 * ЗАЧЕМ ОТДЕЛЬНАЯ ДВЕРЬ. Все адресные ветки публичного плеера идут через
 * `safeHref`, и он пропускает только http/https/mailto. Кнопке возврата в
 * приложение (`return_to_app_button`) и кнопке «Закрыть встроенное окно»
 * (`close_webview_button`) нужна СХЕМА ПРИЛОЖЕНИЯ (`myapp://…`), поэтому их
 * когда-то сделали в обход всякой очистки: `href={block.url || appSchemeUrl}`
 * и `window.location.href = block.url`. После пачки 18 схема бэка принимает
 * служебный маркер `w2a://store` во ВСЕХ адресных полях (иначе реестр
 * адресных полей терял бы `button_link.url`), и маркер, попавший в `url`
 * такой кнопки копипастом автора или правкой ИИ-чата, уехал бы покупателю
 * мёртвым `href="w2a://store"`.
 *
 * ПРАВИЛО. Пропускаем адрес, если верно ОДНО из двух:
 *   1) его пропускает ОБЩАЯ дверь `safeHref` (http/https/mailto,
 *      относительные адреса) — её политику мы не переписываем и не копируем,
 *      а зовём: вторая копия правила и есть то, из чего потом вырастает
 *      расхождение (шрам WEB-1687);
 *   2) его схема ТОЧНО совпадает со схемой приложения ЭТОГО проекта — той,
 *      что пришла контекстом (`bridgeConfig`/`appSchemeUrl`).
 * Всё прочее — включая маркер `w2a://store`, чужую схему и `javascript:` —
 * получает запасное значение (`undefined`): у кнопки не будет href / она не
 * нарисуется, но мёртвого адреса покупатель не увидит.
 *
 * ЧЕГО ЗДЕСЬ НЕТ НАМЕРЕННО. Общий `isAllowedUrl` на произвольные схемы НЕ
 * расширяется: разрешение на `myapp://` действует ровно в этих двух ветках и
 * ровно для схемы того проекта, чью страницу смотрит покупатель.
 *
 * ВТОРОЙ КОНЕЦ — СЕРВЕР (WEB-1911, возврат приёмки 07.09). Пока сервер держал
 * те же два поля на общем списке схем, кабинет обещал автору принятым адрес,
 * который запись отбивала 400-м (а с ним вставало автосохранение всего
 * черновика). Теперь у этих полей своя схема записи `appLinkUrlOptional`
 * (`backend/src/ai-funnels/block-schema/url-allowlist.ts`): она пускает то же,
 * что общая дверь, ПЛЮС любую схему вне запретного списка. Точного совпадения
 * со схемой проекта сервер не проверяет — контекста проекта у схемы блока нет;
 * сужение остаётся здесь, на показе. Список `NEVER_ALLOWED_SCHEMES` ниже —
 * зеркало серверного, равенство множеств в обе стороны держит сторож
 * `backend/src/ai-funnels/block-schema/app-link-url.web1911.spec.ts` (он читает
 * ЭТОТ файл, поэтому список нельзя поменять здесь в одиночку).
 *
 * КОНТЕКСТ — НЕ МАНДАТ. Даже если схема из контекста совпала, опасные схемы
 * (`javascript:`, `data:`, …) не проходят: настройки проекта — тоже вход, и
 * подставленный туда `javascript:` не должен становиться адресом кнопки.
 */
import { safeHref, stripControlAndSpace, stripInvisibleOnly } from './isAllowedUrl';
import { STORE_AUTO_URL } from './storeAutoUrl';

/**
 * Схемы, которые не становятся адресом ни при каком совпадении с контекстом.
 * Зеркалит смысл запрета в `isAllowedUrl` (там список РАЗРЕШЁННЫХ, здесь —
 * страховка ветки «схема приложения», у которой белого списка нет по природе:
 * схема у каждого проекта своя).
 */
const NEVER_ALLOWED_SCHEMES: ReadonlySet<string> = new Set([
  'javascript',
  'data',
  'vbscript',
  'file',
  'blob',
]);

const SCHEME_RE = /^([a-zA-Z][a-zA-Z0-9+.-]*):/;

/** Схема адреса в нижнем регистре, либо null у адреса без схемы. */
function schemeOf(cleaned: string): string | null {
  const match = SCHEME_RE.exec(cleaned);
  return match ? match[1].toLowerCase() : null;
}

export function appLinkHref(
  raw: string | null | undefined,
  appSchemeUrl: string | null | undefined,
): string | undefined {
  if (typeof raw !== 'string') return undefined;
  // Сверяем ОЧИЩЕННОЕ значение той же функцией, что и общая дверь: иначе
  // `w2a:/<zero-width>/store` прошёл бы мимо точного сравнения ровно так же,
  // как обходят список схем (ревизия пачки 18, safeHref).
  const cleaned = stripControlAndSpace(raw);
  if (cleaned.length === 0) return undefined;
  // Неподставленная переменная — не адрес (то же правило D7, что в
  // isAllowedUrl): `myapp://open/{{code}}` увёл бы покупателя в никуда.
  if (cleaned.includes('{{') || cleaned.includes('}}')) return undefined;
  // Маркер адреса магазина — ПРИЗНАК, а не адрес. Разрешать его тут нельзя
  // ни при каком контексте: подменять маркер настоящим адресом умеет одна
  // ветка (button_link), и эти две кнопки — не она.
  if (cleaned === STORE_AUTO_URL) return undefined;

  // 1) обычные адреса — общей дверью, без второй копии её политики.
  const viaCommon = safeHref(raw, '');
  if (viaCommon !== '') return viaCommon;

  // 2) схема приложения ЭТОГО проекта.
  const scheme = schemeOf(cleaned);
  if (scheme === null || NEVER_ALLOWED_SCHEMES.has(scheme)) return undefined;
  // Свежий взгляд по пачке 18 (проход 2): если у проекта схема приложения НЕ
  // настроена (контекст без `appSchemeUrl` — например, возврат идёт
  // Universal Link'ами), сверять ссылку не с чем, а до WEB-1911 любая схема
  // автора здесь работала. Отбрасывать её значило бы молча убить кнопку у
  // таких проектов. Поэтому без схемы в контексте проходит любая НЕОПАСНАЯ
  // схема (маркер и javascript:/data:/… уже отсечены выше); при настроенной
  // схеме — только точное совпадение с ней.
  // Свежий взгляд по пачке 18 (проход 3): наружу — как у safeHref — адрес без
  // невидимых символов, но С пробелами (параметр диплинка «msg=Спасибо за
  // покупку» не склеивается); схема при этом опознана по полностью очищенной.
  const visible = stripInvisibleOnly(raw).trim();
  if (typeof appSchemeUrl !== 'string' || appSchemeUrl.length === 0) {
    return visible;
  }
  const contextScheme = schemeOf(stripControlAndSpace(appSchemeUrl));
  if (contextScheme === null || NEVER_ALLOWED_SCHEMES.has(contextScheme)) {
    return undefined;
  }
  // Наружу — ОЧИЩЕННОЕ значение, то самое, по которому опознали схему
  // (ревизия пачки 19). Отдавать здесь `raw` значило проверить одну строку, а
  // в `href` поставить другую: `my<zero-width>app://…` доезжал бы до разметки
  // невидимыми символами внутри. Ветка http(s) выше так и делает — `safeHref`
  // возвращает то, что проверил.
  return scheme === contextScheme ? visible : undefined;
}
